import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import ThemeToggle from "@/components/ThemeToggle";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import InstallAppButton from "@/components/InstallAppButton";
import TrialBanner from "@/components/TrialBanner";
import { SetuMark } from "@/components/Logo";
import {
  LayoutDashboard, ScanEye, MessageSquareText, FileText, Boxes,
  ShieldAlert, Users, QrCode, CloudSun, CreditCard, Settings as Cog,
  LogOut, Mic, Menu, X, Fingerprint, Calculator, AlertTriangle,
} from "lucide-react";

const NavItem = ({ to, icon: Icon, label, testid, onNavigate }) => (
  <NavLink
    end
    to={to}
    data-testid={testid}
    onClick={onNavigate}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-2.5 mx-2 my-0.5 rounded-md text-sm transition-colors ${
        isActive
          ? "bg-muted text-foreground font-medium"
          : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
      }`
    }
  >
    <Icon className="h-4 w-4" strokeWidth={1.75} />
    <span>{label}</span>
  </NavLink>
);

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => { setOpen(false); await logout(); nav("/"); };
  const closeSidebar = () => setOpen(false);

  const items = [
    { to: "/app", icon: LayoutDashboard, label: t("dashboard"), testid: "nav-dashboard" },
    { to: "/app/inspector", icon: ScanEye, label: t("photo_inspector"), testid: "nav-inspector" },
    { to: "/app/chat", icon: MessageSquareText, label: t("nav_copilot"), testid: "nav-chat" },
    { to: "/app/reports", icon: FileText, label: t("daily_reports"), testid: "nav-reports" },
    { to: "/app/boq", icon: Calculator, label: t("nav_boq"), testid: "nav-boq" },
    { to: "/app/delay", icon: AlertTriangle, label: t("nav_delay"), testid: "nav-delay" },
    { to: "/app/attendance", icon: Fingerprint, label: t("nav_attendance"), testid: "nav-attendance" },
    { to: "/app/voice", icon: Mic, label: t("nav_voice"), testid: "nav-voice" },
    { to: "/app/inventory", icon: Boxes, label: t("inventory"), testid: "nav-inventory" },
    { to: "/app/safety", icon: ShieldAlert, label: t("safety"), testid: "nav-safety" },
    { to: "/app/team", icon: Users, label: t("team"), testid: "nav-team" },
    { to: "/app/equipment", icon: QrCode, label: t("equipment"), testid: "nav-equipment" },
    { to: "/app/weather", icon: CloudSun, label: t("weather"), testid: "nav-weather" },
    { to: "/app/subscription", icon: CreditCard, label: t("billing"), testid: "nav-subscription" },
    { to: "/app/settings", icon: Cog, label: t("settings"), testid: "nav-settings" },
  ];

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Mobile backdrop overlay when sidebar is open */}
      {open && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-30"
          onClick={closeSidebar}
          data-testid="sidebar-backdrop"
          aria-hidden
        />
      )}

      {/* Sidebar */}
      <aside
        className={`${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 fixed md:static top-0 left-0 z-40 h-screen w-72 md:w-64 border-r border-border bg-card transition-transform duration-200`}
      >
        <div className="h-14 sm:h-16 flex items-center justify-between px-4 sm:px-5 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-primary grid place-items-center text-primary-foreground"><SetuMark size={22} strokeWidth={2.6} /></div>
            <div>
              <div className="font-heading text-base leading-tight tracking-tight">Setubuild.OS</div>
              <div className="mono text-[9px] text-muted-foreground uppercase tracking-[0.15em]">The bridge</div>
            </div>
          </div>
          <button className="md:hidden p-2 -mr-2" onClick={closeSidebar} data-testid="sidebar-close-btn" aria-label="Close menu">
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="py-2 overflow-y-auto h-[calc(100vh-3.5rem)] md:h-[calc(100vh-4rem)]">
          {items.map((it) => <NavItem key={it.to} {...it} onNavigate={closeSidebar} />)}
          <div className="mt-6 px-3">
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="w-full btn-sharp inline-flex items-center gap-2 border border-border px-3 py-2 text-sm hover:bg-muted"
            >
              <LogOut className="h-4 w-4" /> {t("logout")}
            </button>
          </div>
        </nav>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 sm:h-16 border-b border-border bg-card flex items-center justify-between px-3 sm:px-4 md:px-8 sticky top-0 z-20">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button
              className="md:hidden p-2 -ml-2 rounded-md hover:bg-muted transition"
              onClick={() => setOpen(true)}
              data-testid="sidebar-open-btn"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <div className="hidden md:block mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{t("signed_in_as")}</div>
              <div className="font-heading text-sm truncate">
                {user?.name}
                <span className="mono text-primary text-[10px] tracking-widest ml-1.5">{user?.subscription?.plan?.toUpperCase() || "FREE"}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <InstallAppButton />
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
        </header>
        <main className="flex-1">
          <TrialBanner />
          <div className="p-4 md:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
